<template>
  <Modal ref="modal" title="Informações da fatura">
    <div v-if="loading" class="d-flex justify-content-center">
      <component :animation-duration="1000" :is="'SpringSpinner'" :color="'#1C2D5A'" :size="80" class="mx-2">
      </component>
    </div>

    <div v-else class="info-container">
      <div class="info-row">
        <Input legend="Status" :modelValue="fatura.status" :readonly="true" />
        <Input legend="Processo" :modelValue="fatura.tipo" :readonly="true" />
      </div>
      <div class="info-row">
        <Input legend="Nome do aluno" :modelValue="fatura.nome" :readonly="true" />
        <Input legend="CPF do aluno" :modelValue="utils.formatarCPF(fatura.cpf)" :readonly="true" />
      </div>
      <div class="info-row">
        <Input legend="Código de barras" :modelValue="fatura.codigoBarras || '-'" :readonly="true" data-testid="input-codigo-barras" />
      </div>
      <div class="info-row">
        <Input legend="Valor original" :modelValue="`R$ ${fatura.valorServico || '0,00'}`" :readonly="true" />
        <Input legend="Valor do desconto" :modelValue="`R$ ${fatura.discountValue || '0,00'}`" :readonly="true" />
        <Input legend="Valor do juros" :modelValue="`R$ ${fatura.valorjuros || '0,00'}`" :readonly="true" />
        <Input legend="Valor do multa" :modelValue="`R$ ${fatura.valorMulta || '0,00'}`" :readonly="true" />
        <Input legend="Valor a pagar" :modelValue="`R$ ${fatura.valor || '0,00'}`" :readonly="true" />
        <Input legend="Data do boleto" :modelValue="utils.formatOnlyDate(fatura.created_at) || '-'" :readonly="true" data-testid="input-data-boleto" />
        <Input legend="Data do pagamento" :modelValue="fatura.paid_at || '-'" :readonly="true" data-testid="input-data-pagamento" />
      </div>

      <div class="status-aluno-section" data-testid="faturamento-info-modal-status-aluno">
        <h3>Status do aluno</h3>
        <div v-if="statusLoading" class="d-flex justify-content-center">
          <component :animation-duration="1000" :is="'SpringSpinner'" :color="'#1C2D5A'" :size="40" class="mx-2" />
        </div>
        <template v-else>
          <Table :headers="['Prático', 'Teórico']">
            <tr>
              <td v-if="alunoStatus.bloqueio_pratico" class="status-bloqueado">
                <Close :size="19" /> Bloqueado
              </td>
              <td v-else class="status-ativo">
                <Check :size="19" /> Ativo
              </td>
              <td v-if="alunoStatus.bloqueio_teorico" class="status-bloqueado">
                <Close :size="19" /> Bloqueado
              </td>
              <td v-else class="status-ativo">
                <Check :size="19" /> Ativo
              </td>
            </tr>
          </Table>
        </template>
      </div>
    </div>
  </Modal>
</template>

<script>
import * as spinners from 'epic-spinners'
import Modal from 'core/Modal.vue'
import Input from 'core/Input'
import Table from 'core/Table'
import Close from 'icons/Close'
import Check from 'icons/Check'
import utils from '../../../../services/utils'

export default {
  name: 'FaturamentoInfoModal',
  components: {
    ...spinners,
    Modal,
    Input,
    Table,
    Close,
    Check,
  },
  props: {
    schema: {
      type: String,
      required: false,
      default: '',
    }
  },
  data () {
    return {
      utils: utils,
      fatura: null,
      loading: true,
      statusLoading: false,
      alunoStatus: {
        bloqueio_pratico: null,
        bloqueio_teorico: null,
      },
    }
  },
  methods: {
    async open (fatura) {
      this.$refs.modal.open()
      this.fatura = fatura
      this.loading = false

      await this.fetchAlunoStatus()
    },
    close () {
      this.$refs.modal.close()
    },
    toast (text) {
      this.$toasted.show(text, {
        icon: 'fa-times',
        position: 'top-right',
        duration: 5000,
        fullWidth: false
      })
    },
    async fetchAlunoStatus () {
      try {
        this.statusLoading = true
        this.alunoStatus = { bloqueio_pratico: null, bloqueio_teorico: null }

        // Buscar aluno por CPF para obter o id_aluno
        const alunoRes = await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/alunos/${this.fatura.cpf}`, {
          params: { schema: this.schema }
        })

        const aluno = (alunoRes.body && alunoRes.body.dados) || null

        if (!aluno || !aluno.id_aluno) {
          this.statusLoading = false
          return
        }

        // Buscar detalhes do aluno (inclui bloqueios)
        const detalhesRes = await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/alunos/consultar/detalhes/${aluno.id_aluno}`, {
          params: { schema: this.schema }
        })

        const candidato = detalhesRes.body && detalhesRes.body.dados && detalhesRes.body.dados.candidato

        if (candidato) {
          this.alunoStatus = {
            bloqueio_pratico: !!candidato.bloqueio_pratico,
            bloqueio_teorico: !!candidato.bloqueio_teorico,
          }
        }
      } catch (err) {
        // Silencia erro apenas do bloco de status para não bloquear a visualização da fatura
      } finally {
        this.statusLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.info-container {
  width: 60vw;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  .info-row {
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;

    >div {
      flex: 1;
    }
  }
}

.status-aluno-section {
  margin-top: 1rem;

  h3 {
    margin: 0 0 1rem 0;
    font-size: 1rem;
    font-weight: 600;
    color: #606264;
  }

  .status-bloqueado {
    color: #d9534f;
    font-weight: 500;

    svg {
      vertical-align: middle;
      margin-right: 0.25rem;
    }
  }

  .status-ativo {
    color: #28a745;
    font-weight: 500;

    svg {
      vertical-align: middle;
      margin-right: 0.25rem;
    }
  }
}
</style>
